import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import React, { Component } from "react";
import GoogleSignInButton from "./GoogleSignInButton";
import { Building2, Eye, EyeOff, GraduationCap, Lock, Mail, PenLine, User } from "lucide-react";
import { auth } from "../config/Firebase";
import UsersService from "../services/UsersService";
import { applyAuthSession } from "../utility/applyAuthSession";
import Modal, { FormGroup, FormControl } from "./ui/Modal";
import Button from "./ui/Button";

const ACCOUNT_TYPE_OPTIONS = [
  { value: "student", label: "Student", icon: GraduationCap },
  { value: "college", label: "College", icon: Building2 },
  { value: "author", label: "Author", icon: PenLine },
];

// Left-icon wrapper shared by the text fields below — keeps the icon purely
// decorative (pointer-events-none) so it never intercepts clicks meant for the input.
const IconField = ({ icon: Icon, children }) => (
  <div className="relative">
    <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
    {children}
  </div>
);

const PasswordField = ({ id, value, onChange, isInvalid, show, onToggleShow, placeholder, autoComplete }) => (
  <div className="relative">
    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
    <FormControl
      id={id}
      type={show ? "text" : "password"}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      isInvalid={isInvalid}
      autoComplete={autoComplete}
      className="pl-9 pr-9"
    />
    <button
      type="button"
      onClick={onToggleShow}
      tabIndex={-1}
      aria-label={show ? "Hide password" : "Show password"}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
    >
      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  </div>
);

class SignUpPopUp extends Component {
  constructor(props) {
    super(props);

    this.state = {
      phone: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      accountType: "student",
      otp: "",
      showSignUpModal: true,
      showOtpModal: false,
      showPassword: false,
      showConfirmPassword: false,
      errorMessage: "",
      validationErrors: {
        phone: "",
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        otp: "",
      },
    };

    this.changePhoneNumberHandler = this.changePhoneNumberHandler.bind(this);
    this.changeFullNameHandler = this.changeFullNameHandler.bind(this);
    this.changeEmailHandler = this.changeEmailHandler.bind(this);
    this.changePasswordHandler = this.changePasswordHandler.bind(this);
    this.changeConfirmPasswordHandler = this.changeConfirmPasswordHandler.bind(this);
    this.changeOtpHandler = this.changeOtpHandler.bind(this);
    this.changeAccountTypeHandler = this.changeAccountTypeHandler.bind(this);
    this.verifyPhoneNumber = this.verifyPhoneNumber.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.saveUser = this.saveUser.bind(this);
    this.createUserInBackend = this.createUserInBackend.bind(this);
  }

  isFirebaseOtpDisabled() {
    return String(process.env.REACT_APP_DISABLE_FIREBASE_OTP).toLowerCase() === "true";
  }

  async createUserInBackend() {
    const { phone, username, email, password, confirmPassword, accountType } = this.state;

    const user = {
      phone,
      username,
      email,
      password,
      confirmPassword,
      ...(accountType === "college" ? { role: ["college"] } : {}),
      ...(accountType === "author" ? { role: ["author"] } : {}),
    };

    try {
      const signUpResponse = await UsersService.validateSignup(user);
      if (signUpResponse.data === 0) {
        this.setState({
          validationErrors: {
            ...this.state.validationErrors,
            phone: "Invalid Data",
          },
        });
        return;
      }

      // College accounts require admin approval. Backend will return active=false and no token.
      const active = signUpResponse?.data?.active;
      const tokenValue = signUpResponse?.data?.token;
      const isCollegePending = accountType === "college" && (active === false || !tokenValue);

      if (isCollegePending) {
        this.setState({
          errorMessage:
            "College signup successful. Your account is pending admin approval. Please try logging in after approval.",
        });
        return;
      }

      applyAuthSession(signUpResponse.data);

      this.props.onClose();
      this.props.onDefaultLogin();
    } catch (error) {
      // Must extract a string (.message), not the raw response body — the backend's
      // error responses are JSON objects (e.g. {status, error, message, path}), and
      // setting errorMessage to that object crashes the render below ("Objects are
      // not valid as a React child") instead of showing the actual error text.
      const backendMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : null) ||
        (typeof error?.message === "string" ? error.message : null);
      this.setState({ errorMessage: backendMessage || "Signup failed. Please try again." });
      console.error("Error saving user:", error);
      console.error("Error response:", error?.response);
    }
  }

  // Change handlers for input fields
  changePhoneNumberHandler(event) {
    this.setState({ phone: event.target.value });
  }

  changeFullNameHandler(event) {
    this.setState({ username: event.target.value });
  }

  changeEmailHandler(event) {
    this.setState({ email: event.target.value });
  }

  changePasswordHandler(event) {
    this.setState({ password: event.target.value });
  }

  changeConfirmPasswordHandler(event) {
    this.setState({ confirmPassword: event.target.value });
  }

  changeOtpHandler(event) {
    this.setState({ otp: event.target.value });
  }

  changeAccountTypeHandler(event) {
    this.setState({ accountType: event.target.value });
  }

  // Form validation method
  validateForm() {
    const { phone, username, email, password, confirmPassword, otp } = this.state;
    let valid = true;
    const validationErrors = {
      phone: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      otp: "",
    };

    this.setState({ errorMessage: "" });

    if (!phone) {
      validationErrors.phone = "Phone number is required.";
      valid = false;
    } else if (!/^\d{10}$/.test(phone.trim())) {
      validationErrors.phone = "Enter a valid 10-digit phone number.";
      valid = false;
    }

    if (!username) {
      validationErrors.username = "Full name is required.";
      valid = false;
    }

    if (!email) {
      validationErrors.email = "Email is required.";
      valid = false;
    }

    if (!password) {
      validationErrors.password = "Password is required.";
      valid = false;
    }

    if (password !== confirmPassword) {
      validationErrors.confirmPassword = "Passwords do not match.";
      valid = false;
    }

    if (!otp && this.state.showOtpModal) {
      validationErrors.otp = "OTP is required.";
      valid = false;
    }

    this.setState({ validationErrors });
    return valid;
  }

  // Method to verify phone number and send OTP
  async verifyPhoneNumber() {
    if (this.validateForm()) {
      if (this.isFirebaseOtpDisabled()) {
        // Backend-only signup mode (no SMS OTP). Intended for development only.
        await this.createUserInBackend();
        return;
      }

      const phoneNumber = `+91${this.state.phone}`;
      try {
        if (!this.recaptchaVerifier) {
          this.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
            size: "invisible",
            "expired-callback": () => {
              this.setState({ errorMessage: "reCAPTCHA expired. Please try again." });
            },
          });

          // Ensure the widget is rendered/ready.
          await this.recaptchaVerifier.render();
        }

        const confirmationResult = await signInWithPhoneNumber(
          auth,
          phoneNumber,
          this.recaptchaVerifier
        );
        this.setState({ confirmationResult, showSignUpModal: false, showOtpModal: true });
      } catch (error) {
        const code = error?.code;
        const message = error?.message || "Failed to send OTP. Please try again.";

        const validationErrors = {
          ...this.state.validationErrors,
        };

        if (code === "auth/billing-not-enabled") {
          this.setState({
            errorMessage:
              "Firebase billing is not enabled for this project, so Phone Auth (SMS OTP) cannot be used. Enable billing in Firebase/Google Cloud, or set REACT_APP_DISABLE_FIREBASE_OTP=true to allow backend-only signup (no OTP).",
          });
          this.setState({ validationErrors });
          console.error("Error verifying phone number:", error);
          return;
        }

        if (code === "auth/invalid-phone-number") {
          validationErrors.phone = "Invalid phone number.";
        } else if (code === "auth/too-many-requests") {
          this.setState({ errorMessage: "Too many attempts. Please try again later." });
        } else if (code === "auth/quota-exceeded") {
          this.setState({ errorMessage: "SMS quota exceeded. Please try later." });
        } else if (code === "auth/operation-not-allowed") {
          this.setState({ errorMessage: "Phone authentication is not enabled in Firebase." });
        } else if (code === "auth/app-not-authorized") {
          this.setState({
            errorMessage:
              "This domain is not authorized for Firebase Phone Auth. Add it in Firebase Console → Authentication → Settings → Authorized domains.",
          });
        } else {
          this.setState({ errorMessage: message });
        }

        this.setState({ validationErrors });
        console.error("Error verifying phone number:", error);
      }
    }
  }

  // Method to save user after OTP verification
  async saveUser() {
    if (this.validateForm()) {
      const { phone, username, email, password, confirmPassword, accountType } = this.state;

      if (!this.state.confirmationResult) {
        this.setState({ errorMessage: "Please request OTP first." });
        return;
      }

      try {
        await this.state.confirmationResult.confirm(this.state.otp);
      } catch (error) {
        const code = error?.code;
        const validationErrors = {
          ...this.state.validationErrors,
          otp:
            code === "auth/invalid-verification-code"
              ? "Invalid OTP"
              : "OTP verification failed. Please try again.",
        };
        this.setState({ validationErrors });
        console.error("OTP verification error:", error);
        return;
      }

      await this.createUserInBackend();
    }
  }

  toggleShowPassword = () => this.setState((s) => ({ showPassword: !s.showPassword }));

  toggleShowConfirmPassword = () => this.setState((s) => ({ showConfirmPassword: !s.showConfirmPassword }));

  // Called by GoogleSignInButton once the backend has accepted the Google sign-in (including
  // the mobile-number step for first-time Google accounts) and returned a session.
  handleGoogleSuccess = (data) => {
    const { roles } = applyAuthSession(data);
    this.props.onClose();
    if (roles.includes("ROLE_ADMIN") && this.props.onAdminLogin) {
      this.props.onAdminLogin();
    } else {
      this.props.onDefaultLogin();
    }
  };

  render() {
    const { validationErrors, showSignUpModal, showOtpModal, showPassword, showConfirmPassword } = this.state;

    return (
      <>
        <Modal
          show={showSignUpModal}
          onClose={this.props.onClose}
          maxWidth="max-w-lg"
          title={
            <span className="flex items-center gap-2">
              <img src="/images/bvlogo.png" alt="BharatVidyarthi" className="h-6 w-6 object-contain" />
              Create your account
            </span>
          }
          footer={
            <>
              <div id="recaptcha-container"></div>
              <Button variant="primary" onClick={this.verifyPhoneNumber} className="w-full">
                Create account
              </Button>
              <p className="mt-3 text-center text-xs text-gray-400">
                By signing up you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-brand-600 hover:underline">
                  Terms
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-brand-600 hover:underline">
                  Privacy Policy
                </a>
                .
              </p>
            </>
          }
        >
          <p className="-mt-1 mb-4 text-sm text-gray-500">
            Free to join — save colleges, compare options, and track your applications in one place.
          </p>

          {this.state.errorMessage ? (
            <div className="mb-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" role="alert">
              {typeof this.state.errorMessage === "string"
                ? this.state.errorMessage
                : "Something went wrong. Please try again."}
            </div>
          ) : null}

          <form onSubmit={(e) => e.preventDefault()} className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormGroup label="Full Name" htmlFor="formFullName" error={validationErrors.username} className="!mt-0">
                <IconField icon={User}>
                  <FormControl
                    id="formFullName"
                    type="text"
                    placeholder="Enter full name"
                    value={this.state.username}
                    onChange={this.changeFullNameHandler}
                    isInvalid={validationErrors.username !== ""}
                    className="pl-9"
                  />
                </IconField>
              </FormGroup>

              <FormGroup label="Phone number" htmlFor="formPhone" error={validationErrors.phone} className="!mt-0">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                    +91
                  </span>
                  <FormControl
                    id="formPhone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit mobile number"
                    value={this.state.phone}
                    onChange={this.changePhoneNumberHandler}
                    isInvalid={validationErrors.phone !== ""}
                    className="pl-11"
                  />
                </div>
              </FormGroup>
            </div>

            <FormGroup label="Email" htmlFor="formEmail" error={validationErrors.email} className="!mt-0">
              <IconField icon={Mail}>
                <FormControl
                  id="formEmail"
                  type="email"
                  placeholder="Enter email"
                  value={this.state.email}
                  onChange={this.changeEmailHandler}
                  isInvalid={validationErrors.email !== ""}
                  className="pl-9"
                />
              </IconField>
            </FormGroup>

            <FormGroup label="I am a" className="!mt-0">
              <div className="grid grid-cols-3 gap-2">
                {ACCOUNT_TYPE_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const selected = this.state.accountType === option.value;
                  return (
                    <label
                      key={option.value}
                      htmlFor={`accountType-${option.value}`}
                      className={
                        "flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-3 text-xs font-semibold transition-colors " +
                        (selected
                          ? "border-brand-600 bg-brand-50 text-brand-700"
                          : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50")
                      }
                    >
                      <input
                        type="radio"
                        id={`accountType-${option.value}`}
                        name="accountType"
                        value={option.value}
                        checked={selected}
                        onChange={this.changeAccountTypeHandler}
                        className="sr-only"
                      />
                      <Icon className="h-5 w-5" />
                      {option.label}
                    </label>
                  );
                })}
              </div>
            </FormGroup>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormGroup label="Password" htmlFor="formPassword" error={validationErrors.password} className="!mt-0">
                <PasswordField
                  id="formPassword"
                  placeholder="Enter password"
                  value={this.state.password}
                  onChange={this.changePasswordHandler}
                  isInvalid={validationErrors.password !== ""}
                  show={showPassword}
                  onToggleShow={this.toggleShowPassword}
                  autoComplete="new-password"
                />
              </FormGroup>

              <FormGroup label="Confirm Password" htmlFor="formConfirmPassword" error={validationErrors.confirmPassword} className="!mt-0">
                <PasswordField
                  id="formConfirmPassword"
                  placeholder="Confirm password"
                  value={this.state.confirmPassword}
                  onChange={this.changeConfirmPasswordHandler}
                  isInvalid={validationErrors.confirmPassword !== ""}
                  show={showConfirmPassword}
                  onToggleShow={this.toggleShowConfirmPassword}
                  autoComplete="new-password"
                />
              </FormGroup>
            </div>
          </form>

          <div className="mt-4 flex items-center gap-3 text-xs text-gray-400">
            <div className="h-px flex-1 bg-gray-200" />
            or
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          <div className="mt-4 flex justify-center">
            <GoogleSignInButton
              accountType={this.state.accountType}
              onSuccess={this.handleGoogleSuccess}
              onError={(message) => this.setState({ errorMessage: message })}
            />
          </div>
        </Modal>

        {/* OTP Modal */}
        <Modal
          show={showOtpModal}
          onClose={() => this.setState({ showOtpModal: false })}
          title="Verify your phone"
          footer={
            <Button variant="primary" onClick={this.saveUser} className="w-full">
              Verify OTP
            </Button>
          }
        >
          <p className="-mt-1 mb-4 text-sm text-gray-500">
            We sent a 6-digit code to <span className="font-medium text-gray-700">+91 {this.state.phone}</span>.
          </p>
          <form onSubmit={(e) => e.preventDefault()}>
            <FormGroup label="Enter OTP" htmlFor="formOtp" error={validationErrors.otp} className="!mt-0">
              <FormControl
                id="formOtp"
                type="number"
                placeholder="6-digit code"
                value={this.state.otp}
                onChange={this.changeOtpHandler}
                isInvalid={validationErrors.otp !== ""}
                className="text-center text-lg tracking-[0.5em]"
              />
            </FormGroup>
          </form>
        </Modal>
      </>
    );
  }

}

export default SignUpPopUp;
